import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Topbar from '../components/Topbar'

// ── App colours (FB blue theme) ───────────────────────────────────────────
const BLUE   = '#1877F2'
const NAVY   = '#0B1F3A'
const TEAL   = '#2dd4b0'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const BORDER = '#E4E6EB'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

// ── Plan definitions ───────────────────────────────────────────────────────
const PLANS = [
  {
    key: 'starter', label: 'Starter',
    desc: 'Get started and manage your orders',
    highlight: false,
    sections: [
      { heading: 'Included', items: [
        { text: 'Unlimited orders', on: true },
        { text: 'Up to 50 products (variants not counted)', on: true },
        { text: 'Inventory management', on: true },
        { text: 'Free website — pre-built, customise with your content and branding', on: true },
        { text: 'Customer tracking portal', on: true },
        { text: 'Basic analytics', on: true },
        { text: 'Referral programme', on: true },
      ]},
      { heading: 'Not included', items: [
        { text: 'CRM and customer database', on: false },
        { text: 'PDF receipts', on: false },
        { text: 'Goals and Finance / P&L', on: false },
        { text: 'AdBoost', on: false },
        { text: 'Tara AI Business Agent', on: false },
        { text: 'Staff accounts', on: false },
      ]},
    ],
  },
  {
    key: 'growth', label: 'Growth',
    desc: 'Full analytics, receipts and AdBoost',
    highlight: false, badge: 'Popular',
    sections: [
      { heading: 'Everything in Starter, plus', items: [
        { text: 'Up to 200 products', on: true },
        { text: 'CRM and customer database', on: true },
        { text: 'Full analytics and AI insights', on: true },
        { text: 'PDF receipts', on: true },
        { text: 'Goals and Finance / P&L', on: true },
        { text: 'AdBoost', on: true },
      ]},
      { heading: 'Not included', items: [
        { text: 'Events and fairs', on: false },
        { text: 'Invoices', on: false },
        { text: 'Tara AI Business Agent', on: false },
        { text: 'Staff accounts', on: false },
      ]},
    ],
  },
  {
    key: 'pro', label: 'Pro',
    desc: 'AI, events, invoices and staff access',
    highlight: true, badge: 'Best value',
    sections: [
      { heading: 'Everything in Growth, plus', items: [
        { text: 'Unlimited products', on: true },
        { text: 'Events and fairs module', on: true },
        { text: 'Invoices', on: true },
        { text: 'Tara AI Business Agent — 100 queries/month', on: true },
        { text: 'AI-generated model images — 3 free/month, top up anytime', on: true },
        { text: '3 accounts total — owner + 2 staff', on: true },
        { text: 'Priority support', on: true },
      ]},
    ],
  },
  {
    key: 'business', label: 'Business',
    desc: 'Online + offline — full suite with POS',
    highlight: false, badge: 'Full suite',
    sections: [
      { heading: 'Everything in Pro, plus', items: [
        { text: 'POS terminal for walk-in and in-person sales', on: true },
        { text: 'Up to 3 branches', on: true },
        { text: '6 accounts total — owner + 5 staff', on: true },
        { text: 'Tara AI Business Agent — 500 queries/month', on: true },
        { text: 'AI-generated model images — 10 free/month, top up anytime', on: true },
        { text: 'Dedicated support line', on: true },
      ]},
    ],
  },
]

const FAQS = [
  { q: 'Is there a free trial?', a: 'Yes — every new account gets a 7-day free trial with Pro-level features. No credit card required. One trial per account only.' },
  { q: 'How are products counted?', a: 'Products are counted by product — not by variant. A product with 5 sizes and 3 colours counts as 1 product. Variants are unlimited on all plans.' },
  { q: 'What happens when I hit my product limit?', a: 'You will see a warning from 80% usage. At the limit you can still manage existing products but cannot add new ones until you upgrade.' },
  { q: 'Can I change my plan anytime?', a: 'Yes — upgrade anytime. If you upgrade mid-cycle you only pay the prorated difference for your remaining days. Downgrade simply by choosing a lower plan at your next renewal.' },
  { q: 'What happens when my subscription expires?', a: 'You get a 3-day grace period — access continues while you arrange payment. After that, features lock but your data is fully safe. This grace period is our goodwill to you — please do not rely on it.' },
  { q: 'What is the data retention policy?', a: 'Your data is retained for up to 2 years from your last account activity. After 2 years of inactivity it may be permanently deleted.' },
  { q: 'What payment methods are accepted?', a: 'Bangladesh sellers: bKash, Nagad, Rocket, Upay, debit/credit card, and internet banking — all through our secure payment gateway. International sellers: Visa/Mastercard. Contact us for bank transfer arrangements.' },
  { q: 'Are payments refundable?', a: 'All payments are final and non-refundable. We offer a 7-day free trial so you can evaluate PageConnect fully before committing.' },
  { q: 'What is the Tara AI Business Agent?', a: 'Tara is your AI Business Agent — she knows your actual sales data, stock levels, customers and orders. Ask her questions, get insights, and make smarter decisions. Available from Pro. Need more than your monthly quota? Top up anytime.' },
  { q: 'What are AI-generated model images?', a: 'Upload a flat photo of your product — clothing, saree, jewellery, accessories — and AI places it on a professional model. No photographer or studio needed. Starter pack included in Pro and Business plans. Top up packs available to all paid sellers.' },
  { q: 'Can I use this as a business expense?', a: 'Yes — PageConnect issues valid commercial invoices for all payments. International sellers (Australia, UK, USA, UAE etc.) can typically claim software subscriptions as a business expense. Consult your local tax advisor for your specific situation.' },
  { q: 'Can features or prices change?', a: 'PageConnect reserves the right to modify plans and pricing. Significant changes will be communicated to registered sellers in advance.' },
]

// ── Payment helper ─────────────────────────────────────────────────────────
async function initiatePayment({ seller, planKey, planLabel, amount, currency, billingPeriod }) {
  const { data, error } = await supabase.functions.invoke('create-payment', {
    body: { seller_id: seller.id, seller_email: seller.email || '', seller_name: seller.shop_name || '', seller_phone: seller.phone || '', plan_key: planKey, plan_label: planLabel, amount, currency, billing_period: billingPeriod }
  })
  if (error || !data?.payment_url) throw new Error(error?.message || 'Could not start payment')
  window.location.href = data.payment_url
}

// ── Component ──────────────────────────────────────────────────────────────
function Pricing() {
  const navigate  = useNavigate()
  const { seller, user, getSubscriptionPlan, getPlanLabel, subscriptionDaysLeft, isSubscriptionExpired } = useAuth()

  const [billing, setBilling]         = useState('monthly')
  const [region, setRegion]           = useState('bd')
  const [prices, setPrices]           = useState({})
  const [brand, setBrand]             = useState({ name: 'PageConnect', email: 'help@pageconnect.io', whatsapp: '' })
  const [loading, setLoading]         = useState(true)
  const [payments, setPayments]       = useState([])
  const [openFaq, setOpenFaq]         = useState(null)
  const [payingPlan, setPayingPlan]   = useState(null)
  const [payError, setPayError]       = useState('')
  const [payLoading, setPayLoading]   = useState(false) // full-screen overlay

  const isLoggedIn  = !!user
  const currentPlan = isLoggedIn ? getSubscriptionPlan?.() : null
  const daysLeft    = isLoggedIn ? subscriptionDaysLeft?.() : null
  const expired     = isLoggedIn ? isSubscriptionExpired?.() : false
  const sym         = region === 'bd' ? '৳' : '$'

  useEffect(() => {
    fetchSettings()
    detectRegion()
    if (isLoggedIn) fetchPayments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchSettings = async () => {
    const { data } = await supabase.from('admin_settings').select('*')
    const map = {}
    data?.forEach(s => { map[s.key] = s.value })
    setPrices(map)
    setBrand({ name: map.brand_name || 'PageConnect', email: map.brand_email || 'help@pageconnect.io', whatsapp: map.brand_whatsapp || '' })
    setLoading(false)
  }

  const detectRegion = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/')
      const d   = await res.json()
      setRegion(d.country_code === 'BD' ? 'bd' : 'int')
    } catch { setRegion('bd') }
  }

  const fetchPayments = async () => {
    if (!seller?.id) return
    const { data } = await supabase.from('subscription_payments').select('*').eq('seller_id', seller.id).order('created_at', { ascending: false }).limit(10)
    setPayments(data || [])
  }

  const getMonthly  = (planKey) => Number(prices['price_' + planKey + '_monthly_' + (region === 'bd' ? 'bdt' : 'usd')] || 0)
  const getAnnual   = (planKey) => Number(prices['price_' + planKey + '_annual_' + (region === 'bd' ? 'bdt' : 'usd')] || 0)

  const handleSubscribe = async (plan) => {
    if (!isLoggedIn) { navigate('/signup'); return }
    if (currentPlan === plan.key) return
    setPayError('')
    setPayingPlan(plan.key)
    setPayLoading(true)
    try {
      const monthlyP = getMonthly(plan.key)
      const annualP  = getAnnual(plan.key)
      const amount   = billing === 'annual' ? annualP * 12 : monthlyP
      await initiatePayment({ seller, planKey: plan.key, planLabel: plan.label, amount, currency: region === 'bd' ? 'BDT' : 'USD', billingPeriod: billing })
    } catch (err) {
      setPayError(err.message || 'Payment could not be started. Please try again.')
      setPayingPlan(null)
      setPayLoading(false)
    }
  }

  const STATUS_COLORS = {
    paid:     { bg: '#ECFDF5', text: GREEN },
    pending:  { bg: '#FFFBEB', text: AMBER },
    failed:   { bg: '#FEF2F2', text: RED },
    refunded: { bg: '#F5F3FF', text: '#6D28D9' },
  }

  const card = (extra = {}) => ({ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, ...extra })

  // ── If logged in, render with Topbar (sidebar comes from ProtectedLayout) ──
  const inner = (
    <div style={{ padding: '18px 24px', fontFamily: 'Space Grotesk, sans-serif' }}>

      {/* Payment loading overlay */}
      {payLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: WHITE, animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: WHITE, fontSize: '15px', fontWeight: 600 }}>Taking you to payment...</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12.5px' }}>Please do not close this tab</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Current plan banner — logged in */}
      {isLoggedIn && currentPlan && (
        <div style={{ ...card({ background: NAVY, border: '1px solid ' + TEAL + '30', marginBottom: '20px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }) }}>
          <div>
            <div style={{ fontSize: '11px', color: TEAL, fontWeight: 700, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current plan</div>
            <div style={{ fontSize: '17px', fontWeight: 800, color: WHITE }}>{getPlanLabel?.() || currentPlan}</div>
            {daysLeft !== null && !expired && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{daysLeft} days remaining{seller?.subscription_end ? ' · expires ' + new Date(seller.subscription_end).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</div>}
            {expired && <div style={{ fontSize: '12px', color: '#FCA5A5', marginTop: '2px', fontWeight: 600 }}>Expired — choose a plan below to continue</div>}
          </div>
          {currentPlan === 'trial' && !expired && daysLeft !== null && (
            <div>
              <div style={{ width: '130px', height: '5px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: '3px', background: TEAL, width: Math.max(5, (daysLeft / 7) * 100) + '%' }} />
              </div>
              <div style={{ fontSize: '11px', color: TEAL, marginTop: '4px' }}>{daysLeft} of 7 trial days left</div>
            </div>
          )}
        </div>
      )}

      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '24px', fontWeight: 800, color: TEXT, marginBottom: '6px', letterSpacing: '-0.4px' }}>Plans and pricing</div>
        <div style={{ fontSize: '13.5px', color: MUTED, marginBottom: '18px' }}>7-day free trial on every account · No credit card required · No hidden fees</div>

        {/* Billing toggle */}
        <div style={{ display: 'inline-flex', padding: '3px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE }}>
          {[{ key: 'monthly', label: 'Monthly' }, { key: 'annual', label: 'Annual' }].map(b => (
            <button key={b.key} onClick={() => setBilling(b.key)}
              style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.15s', background: billing === b.key ? BLUE : 'transparent', color: billing === b.key ? WHITE : MUTED }}>
              {b.label}
            </button>
          ))}
        </div>

        {/* Region toggle */}
        <div style={{ display: 'inline-flex', padding: '3px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, marginLeft: '8px' }}>
          {[{ key: 'bd', label: '🇧🇩 BDT' }, { key: 'int', label: '🌍 USD' }].map(r => (
            <button key={r.key} onClick={() => setRegion(r.key)}
              style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.15s', background: region === r.key ? NAVY : 'transparent', color: region === r.key ? WHITE : MUTED }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {payError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: RED, textAlign: 'center' }}>
          {payError} — <a href={'mailto:' + brand.email} style={{ color: RED, fontWeight: 600 }}>contact support</a>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2.5px solid ' + BLUE, borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite', margin: '0 auto' }} />
        </div>
      ) : (
        <>
          {/* Pricing cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }} className="pricing-grid">
            {PLANS.map(plan => {
              const monthly   = getMonthly(plan.key)
              const annual    = getAnnual(plan.key)
              const display   = billing === 'annual' ? annual : monthly
              const isCurrent = currentPlan === plan.key
              const isPaying  = payingPlan === plan.key

              return (
                <div key={plan.key} style={{ background: WHITE, borderRadius: '14px', border: plan.highlight ? '2px solid ' + BLUE : isCurrent ? '2px solid ' + NAVY : '1px solid ' + BORDER, display: 'flex', flexDirection: 'column', boxShadow: plan.highlight ? '0 4px 20px rgba(24,119,242,0.15)' : 'none', position: 'relative' }}>

                  {plan.badge && (
                    <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', fontSize: '10.5px', fontWeight: 700, padding: '3px 12px', borderRadius: '20px', whiteSpace: 'nowrap', background: plan.highlight ? BLUE : plan.key === 'business' ? AMBER : NAVY, color: WHITE }}>
                      {plan.badge}
                    </div>
                  )}

                  {/* Header */}
                  <div style={{ padding: '20px 18px 14px', borderBottom: '1px solid ' + BORDER }}>
                    <div style={{ fontSize: '15px', fontWeight: 800, color: TEXT, marginBottom: '3px' }}>{plan.label}</div>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '14px', lineHeight: 1.5 }}>{plan.desc}</div>

                    {/* Price */}
                    {billing === 'annual' && monthly > 0 && (
                      <div style={{ fontSize: '12px', color: '#CBD5E1', textDecoration: 'line-through', marginBottom: '1px' }}>{sym}{monthly}/mo</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '26px', fontWeight: 900, color: TEXT, letterSpacing: '-1px' }}>{sym}{display}</span>
                      <span style={{ fontSize: '12px', color: MUTED, marginBottom: '3px' }}>/mo</span>
                    </div>
                    {billing === 'annual' && display > 0 && (
                      <div style={{ fontSize: '11.5px', color: MUTED }}>Billed {sym}{annual * 12}/year</div>
                    )}
                    {billing === 'monthly' && monthly > 0 && (
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Switch to annual to save</div>
                    )}
                  </div>

                  {/* Features */}
                  <div style={{ padding: '14px 18px', flex: 1 }}>
                    {plan.sections.map((sec, si) => (
                      <div key={si} style={{ marginBottom: si < plan.sections.length - 1 ? '12px' : 0 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '7px' }}>{sec.heading}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {sec.items.map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, flexShrink: 0, marginTop: '1px', color: f.on ? TEAL : '#E2E8F0' }}>{f.on ? '✓' : '–'}</span>
                              <span style={{ fontSize: '11.5px', color: f.on ? TEXT : '#CBD5E1', lineHeight: 1.4 }}>{f.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div style={{ padding: '14px 18px 18px' }}>
                    {isCurrent ? (
                      <div style={{ width: '100%', padding: '10px', borderRadius: '9px', border: '2px solid ' + BLUE, color: BLUE, background: BLUE + '08', fontSize: '12.5px', fontWeight: 700, textAlign: 'center', boxSizing: 'border-box' }}>✓ Current plan</div>
                    ) : (
                      <button onClick={() => handleSubscribe(plan)} disabled={isPaying || payLoading}
                        style={{ width: '100%', padding: '11px', borderRadius: '9px', border: 'none', fontSize: '13px', fontWeight: 700, cursor: isPaying || payLoading ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, sans-serif', opacity: isPaying ? 0.7 : 1, transition: 'all 0.15s', background: plan.highlight ? BLUE : NAVY, color: WHITE }}>
                        {isPaying ? 'Please wait...' : isLoggedIn ? (expired ? 'Subscribe' : 'Choose ' + plan.label) : 'Start free trial'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Enterprise */}
          <div style={{ ...card({ padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }) }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT }}>Enterprise</div>
                <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: BG, color: MUTED }}>Custom pricing</span>
              </div>
              <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.6 }}>Chains, franchises and large operations. Unlimited branches, custom SLA, dedicated onboarding and volume pricing.</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {brand.whatsapp && (
                <a href={'https://wa.me/' + brand.whatsapp + '?text=Hi%2C+I%27m+interested+in+PageConnect+Enterprise'} target="_blank" rel="noopener noreferrer"
                  style={{ padding: '9px 16px', borderRadius: '9px', background: '#ECFDF5', color: GREEN, border: '1px solid #BBF7D0', fontSize: '12.5px', fontWeight: 700, textDecoration: 'none', display: 'block' }}>
                  WhatsApp us
                </a>
              )}
              <a href={'mailto:' + brand.email + '?subject=Enterprise+enquiry'}
                style={{ padding: '9px 16px', borderRadius: '9px', background: NAVY, color: WHITE, fontSize: '12.5px', fontWeight: 700, textDecoration: 'none', display: 'block' }}>
                Email us
              </a>
            </div>
          </div>

          {/* Payment methods */}
          <div style={{ ...card({ padding: '18px 20px', marginBottom: '16px' }) }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>Accepted payment methods</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="payment-grid">
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '8px' }}>Bangladesh</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {['bKash', 'Nagad', 'Rocket', 'Upay', 'Debit / Credit card', 'Internet banking'].map(m => (
                    <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: TEAL, flexShrink: 0 }} />
                      <span style={{ fontSize: '12.5px', color: TEXT }}>{m}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '8px' }}>International</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {['Visa / Mastercard', 'Bank transfer (contact us)'].map(m => (
                    <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: TEAL, flexShrink: 0 }} />
                      <span style={{ fontSize: '12.5px', color: TEXT }}>{m}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '10px', fontSize: '11.5px', color: MUTED, lineHeight: 1.6 }}>
                  International sellers — email us at <a href={'mailto:' + brand.email} style={{ color: BLUE }}>{brand.email}</a> to arrange payment.
                </div>
              </div>
            </div>
          </div>

          {/* Trust strips */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }} className="trust-grid">
            {[
              { title: '7-day free trial', desc: 'Full Pro access. No credit card. One trial per account.' },
              { title: '3-day grace period', desc: 'After expiry you get 3 days to renew before features lock. This is our goodwill to you.' },
              { title: 'Data retained up to 2 years', desc: 'Data is safe for up to 2 years of inactivity. Resubscribe anytime and pick up exactly where you left off.' },
            ].map((s, i) => (
              <div key={i} style={{ ...card({ padding: '14px 16px' }) }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>{s.title}</div>
                <div style={{ fontSize: '11.5px', color: MUTED, lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>

          {/* Region note */}
          <div style={{ ...card({ padding: '12px 16px', marginBottom: '16px' }) }}>
            <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.7 }}>
              Pricing is localised by region. You are seeing <strong style={{ color: TEXT }}>{region === 'bd' ? 'Bangladesh (BDT ৳)' : 'International (USD $)'}</strong> pricing.
              <button onClick={() => setRegion(r => r === 'bd' ? 'int' : 'bd')} style={{ marginLeft: '8px', color: BLUE, background: 'none', border: 'none', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, fontFamily: 'Space Grotesk, sans-serif' }}>
                Switch to {region === 'bd' ? 'USD $' : 'BDT ৳'} →
              </button>
            </div>
          </div>

          {/* Payment history — logged in only */}
          {isLoggedIn && (
            <div style={{ ...card({ overflow: 'hidden', marginBottom: '16px' }) }}>
              <div style={{ padding: '12px 18px', background: BG, borderBottom: '1px solid ' + BORDER }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT }}>Payment history</div>
              </div>
              {payments.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>No payments yet</div>
                  <div style={{ fontSize: '12px', color: MUTED }}>Your payment history will appear here once you subscribe</div>
                </div>
              ) : payments.map((p, i) => {
                const sc = STATUS_COLORS[p.status] || { bg: '#F9FAFB', text: MUTED }
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 18px', borderBottom: i < payments.length - 1 ? '1px solid #F9F9F9' : 'none', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{p.plan_label || p.plan_key}</div>
                      <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{p.billing_period === 'annual' ? 'Annual' : 'Monthly'} · {new Date(p.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{p.currency === 'bdt' ? '৳' : '$'}{Number(p.amount || 0).toLocaleString()}</div>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '20px', background: sc.bg, color: sc.text }}>{p.status?.charAt(0).toUpperCase() + p.status?.slice(1)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* FAQ */}
          <div style={{ ...card({ overflow: 'hidden', marginBottom: '16px' }) }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>Common questions</div>
            </div>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ borderBottom: i < FAQS.length - 1 ? '1px solid ' + BORDER : 'none' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', padding: '13px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: TEXT, paddingRight: '14px' }}>{faq.q}</span>
                  <span style={{ color: BLUE, fontSize: '15px', flexShrink: 0, display: 'inline-block', transform: openFaq === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>↓</span>
                </button>
                {openFaq === i && <div style={{ padding: '0 18px 13px', fontSize: '12.5px', color: MUTED, lineHeight: 1.75 }}>{faq.a}</div>}
              </div>
            ))}
          </div>

          {/* Contact */}
          <div style={{ ...card({ padding: '18px 20px', marginBottom: '16px', textAlign: 'center' }) }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>Need help choosing the right plan?</div>
            <div style={{ fontSize: '12.5px', color: MUTED, marginBottom: '14px' }}>Talk to us — we will help you find the best fit for your business.</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {brand.whatsapp && (
                <a href={'https://wa.me/' + brand.whatsapp} target="_blank" rel="noopener noreferrer"
                  style={{ padding: '9px 16px', borderRadius: '9px', background: '#ECFDF5', color: GREEN, border: '1px solid #BBF7D0', fontSize: '12.5px', fontWeight: 600, textDecoration: 'none' }}>
                  WhatsApp us
                </a>
              )}
              <a href={'mailto:' + brand.email}
                style={{ padding: '9px 16px', borderRadius: '9px', background: NAVY, color: WHITE, fontSize: '12.5px', fontWeight: 600, textDecoration: 'none' }}>
                Email us
              </a>
            </div>
          </div>

          {/* Legal */}
          <div style={{ fontSize: '11.5px', color: '#94A3B8', lineHeight: 1.8, marginBottom: '8px' }}>
            All PageConnect subscription payments are final and non-refundable. A 3-day grace period is provided after expiry as a goodwill gesture and does not constitute a right. Data is retained for up to 2 years of inactivity. PageConnect reserves the right to modify plans and pricing at any time with advance notice. Platform provided on an as-is basis. Pricing is localised by region.
          </div>
        </>
      )}

      <style>{`
        @media(max-width:900px){.pricing-grid{grid-template-columns:repeat(2,1fr)!important}}
        @media(max-width:560px){.pricing-grid{grid-template-columns:1fr!important}.trust-grid{grid-template-columns:1fr!important}.payment-grid{grid-template-columns:1fr!important}}
      `}</style>
    </div>
  )

  // Logged-in sellers see Topbar (sidebar from ProtectedLayout)
  if (isLoggedIn) {
    return (
      <div className="flex-1 overflow-x-hidden" style={{ background: BG, fontFamily: 'Space Grotesk, sans-serif' }}>
        <Topbar title="Plans and pricing" />
        {inner}
      </div>
    )
  }

  // Logged-out visitors see standalone nav
  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, sans-serif' }}>
      <div style={{ background: NAVY }}>
        <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: '16px' }}>PageConnect</div>
            <div style={{ color: TEAL, fontSize: '11px', marginTop: '1px' }}>pageconnect.io</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '12.5px', padding: '8px 14px', borderRadius: '9px', background: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.2)', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Sign in</button>
            <button onClick={() => navigate('/signup')} style={{ fontSize: '12.5px', padding: '8px 16px', borderRadius: '9px', background: TEAL, color: NAVY, border: 'none', fontWeight: 700, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Start free trial</button>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: '1060px', margin: '0 auto' }}>
        {inner}
      </div>
    </div>
  )
}

export default Pricing